import { RouteType } from "types/RouteType";
import type PermissionsStore from "./PermissionsStore";

/**
 * Administration → Page Visibility: per-user page hiding.
 *
 * Admin-only. The body lists the team and, per user, every manageable page
 * with a Visible switch. Turning a page off writes a `user-permissions` deny
 * row that `PageAccessStore` + the route guard read IN THE BROWSER to bounce
 * that user to `Unauthorized?reason=page_blocked`.
 *
 * THE BOUNDARY (say it the same way everywhere — see packages/web/CLAUDE.md
 * "Page visibility is not a security boundary"):
 *
 *   Page visibility hides pages in this app's UI. It is NOT a security
 *   boundary: what an account may read or change through the API is governed
 *   by its role/tier, which the server enforces on every request.
 *
 * Default-visible: no rows means nothing is hidden.
 *
 * NAMING: the route/header/nav all read "Page Visibility". The file, the class
 * (`PermissionsStore`) and the registered store key (`permissionsStore`) still
 * read "Permissions" because that key is asserted in
 * `views/administration/administrationStoreList.test.ts` and the DB table
 * (`user_permissions`) and API service path (`user-permissions`) are a
 * deliberate non-rename. Rename what a human reads, not the wire format.
 */
const permissionsRoute: RouteType[] = [
  {
    name: "PageVisibility",
    path: "/page-visibility",
    layout: "/app",
    viewportCard: {
      header: {
        title: "Page Visibility",
        enableRefreshButton: true,
        enableBackButton: true,
      },
      body: {
        component: () => import("./Permissions.Body"),
      },
    },
    group: "Admin",
    // The old vocabulary ("permissions", "page access", "blocked") stays
    // searchable: an admin who learned the feature under the security-flavoured
    // name must still be able to find it under the honest one.
    searchAliases: [
      "permissions",
      "user permissions",
      "page access",
      "user access",
      "blocked",
      "hide pages",
      "hidden pages",
      "navigation",
    ],
    requiresAuth: true,
    requiresPermission: false,
    requiresAdmin: true,
    channels: ["user", "departments"],
    beforeEnter: async ({ rootStore }) => {
      const store = (
        rootStore as unknown as { permissionsStore?: PermissionsStore }
      ).permissionsStore;
      void store?.initialize();
      return true;
    },
  },
];

export default permissionsRoute;
